import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Opens a URL in a new window/tab with popup blocker detection.
 * Falls back to navigating the current window if popup is blocked.
 */
export function safeWindowOpen(url: string, target = '_blank', features?: string): void {
  const newWindow = window.open(url, target, features);
  if (!newWindow || newWindow.closed) {
    // Popup was blocked — navigate in current tab as fallback
    window.location.href = url;
  }
}

/**
 * Safe localStorage.setItem that catches QuotaExceededError and SecurityError.
 */
export function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // QuotaExceededError or SecurityError — silently fail
  }
}

/**
 * Safe localStorage.getItem that catches SecurityError (Safari private browsing, cross-origin iframes).
 */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Safe localStorage.removeItem that catches SecurityError.
 */
export function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // SecurityError — localStorage unavailable, silently fail
  }
}

/** Strip markdown formatting from text for plain-text display (e.g. blog excerpts). */
export function stripMarkdown(text: string): string {
  return text
    .replace(/<[^>]+>/g, '')           // HTML tags
    .replace(/^#{1,6}\s+/gm, '')       // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')   // bold
    .replace(/\*(.+?)\*/g, '$1')       // italic
    .replace(/__(.+?)__/g, '$1')       // bold (underscore)
    .replace(/_(.+?)_/g, '$1')         // italic (underscore)
    .replace(/~~(.+?)~~/g, '$1')       // strikethrough
    .replace(/`(.+?)`/g, '$1')         // inline code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // images
    .replace(/^\s*[-*+]\s+/gm, '')     // unordered lists
    .replace(/^\s*\d+\.\s+/gm, '')     // ordered lists
    .replace(/^>\s+/gm, '')            // blockquotes
    .replace(/\s{2,}/g, ' ')           // collapse whitespace
    .trim();
}

/** Format a date string consistently using Intl.DateTimeFormat */
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  }).format(d);
}


const eurFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' });

/** Format a number as EUR currency (e.g. €1.99) */
export function formatCurrency(amount: number): string {
  return eurFormatter.format(amount);
}

/** Format raw credit ledger reason strings for user-friendly display */
export function formatCreditReason(reason: string): string {
  const reasonMap: Record<string, string> = {
    'pro_playlist_generation': 'Expert Curation generation',
    'apple_music_survey_completion': 'Apple Music survey',
    'test': 'Test transaction',
  };

  if (reasonMap[reason]) return reasonMap[reason];
  if (reason.includes('Referral bonus')) return reason;
  if (reason.includes('Coin purchase')) return 'Coin purchase';
  if (reason.includes('Gift bonus')) return 'Gift bonus';
  if (reason.includes('Coupon redemption')) return reason.replace('Coupon redemption:', 'Coupon:');

  return reason
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
